import React, { useEffect, useState, useCallback } from 'react'
import { getApiUrl, API_ENDPOINTS } from '../../config/api'

const ADMIN_KEY = 'persona-admin-2024'

// YouTube 링크 추출 함수 (blocks_json에서 추출)
const extractYouTubeLinks = (content, blocksJson) => {
  const links = []
  const videoBlocks = []
  
  // 1. blocks_json에서 비디오 블록 찾기
  if (blocksJson && blocksJson !== 'null' && blocksJson !== 'undefined') {
    try {
      const blocks = typeof blocksJson === 'string' ? JSON.parse(blocksJson) : blocksJson
      
      if (Array.isArray(blocks)) {
        blocks.forEach(block => {
          if (block && block.type === 'video' && block.url) {
            links.push(block.url)
            videoBlocks.push(block)
          }
        })
      }
    } catch (error) {
      console.error('blocks_json 파싱 오류:', error)
    }
  }
  
  // 2. 기존 content에서도 YouTube 링크 추출 (하위 호환성)
  if (content) {
    const youtubeRegex = /(https?:\/\/(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)[a-zA-Z0-9_-]+(?:\?[^#\s]*)?(?:#[^\s]*)?)/g
    const matches = content.match(youtubeRegex)
    if (matches) {
      links.push(...matches)
    }
  }
  
  
  return {
    links: [...new Set(links)], // 중복 제거
    videoBlocks: videoBlocks
  }
}

// YouTube 비디오 ID 추출 함수 (YoutubeVideosApi.jsx와 동일한 패턴)
const extractVideoId = (url) => {
  if (!url) return null
  
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
  const match = url.match(regExp)
  
  if (match && match[2].length === 11) {
    return match[2]
  }
  
  return null
}

// YouTube 썸네일 URL 생성 함수
const getYouTubeThumbnail = (videoId) => {
  if (!videoId) return ''
  // 고품질 썸네일 URL 반환
  return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
}

// YouTube 썸네일 대체 URL 생성 함수 (고품질이 없을 경우)
const getYouTubeThumbnailFallback = (videoId) => {
  if (!videoId) return ''
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
}

const PostConfirmation = () => {
  console.log('🚀 [PostConfirmation] 컴포넌트가 로드되었습니다!')
  
  const [tab, setTab] = useState('pending') // 'pending' | 'confirmed' | 'all'
  const [pending, setPending] = useState([])
  const [confirmed, setConfirmed] = useState([])
  const [all, setAll] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [query, setQuery] = useState('')
  const [youtubeUrl, setYoutubeUrl] = useState('')
  const [previewVideoId, setPreviewVideoId] = useState(null)
  const [showUrlPreview, setShowUrlPreview] = useState(false)

  const fetchPending = useCallback(async (reset = false) => {
    if (loading) return
    setLoading(true)
    setError('')
    try {
      const targetPage = reset ? 1 : page
      const url = new URL(getApiUrl(API_ENDPOINTS.ADMIN_POSTS_PENDING))
      url.searchParams.set('adminKey', ADMIN_KEY)
      url.searchParams.set('page', String(targetPage))
      url.searchParams.set('limit', '20')
      if (query.trim()) url.searchParams.set('q', query.trim())
      
      console.log('🔍 [CLIENT] API_BASE_URL:', getApiUrl(''))
      console.log('🔍 [CLIENT] import.meta.env.DEV:', import.meta.env.DEV)
      console.log('🔍 [CLIENT] 미확정 게시글 조회 요청:', url.toString())
      
      const res = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })
      console.log('🔍 [CLIENT] 응답 상태:', res.status, res.statusText)
      
      const data = await res.json()
      console.log('🔍 [CLIENT] 응답 데이터:', data)
      
      if (!res.ok) throw new Error(data.error || '목록 조회 실패')
      setPending(prev => reset ? (data.results || []) : [...prev, ...(data.results || [])])
      setHasMore(data.pagination?.hasMore)
      setPage(targetPage + 1)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [loading, page])

  const fetchConfirmed = useCallback(async (reset = false) => {
    if (loading) return
    setLoading(true)
    setError('')
    try {
      const targetPage = reset ? 1 : page
      const url = new URL(getApiUrl(API_ENDPOINTS.ADMIN_POSTS_CONFIRMED))
      url.searchParams.set('adminKey', ADMIN_KEY)
      url.searchParams.set('page', String(targetPage))
      url.searchParams.set('limit', '20')
      if (query.trim()) url.searchParams.set('q', query.trim())
      
      console.log('🔍 [CLIENT] 확정 게시글 조회 요청:', url.toString())
      
      const res = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })
      console.log('🔍 [CLIENT] 응답 상태:', res.status, res.statusText)
      
      const data = await res.json()
      console.log('🔍 [CLIENT] 응답 데이터:', data)
      
      if (!res.ok) throw new Error(data.error || '목록 조회 실패')
      setConfirmed(prev => reset ? (data.results || []) : [...prev, ...(data.results || [])])
      setHasMore(data.pagination?.hasMore)
      setPage(targetPage + 1)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [loading, page])

  const fetchAll = useCallback(async (reset = false) => {
    if (loading) return
    setLoading(true)
    setError('')
    try {
      const targetPage = reset ? 1 : page
      const url = new URL(getApiUrl(API_ENDPOINTS.ADMIN_POSTS_ALL))
      url.searchParams.set('adminKey', ADMIN_KEY)
      url.searchParams.set('page', String(targetPage))
      url.searchParams.set('limit', '20')
      if (query.trim()) url.searchParams.set('q', query.trim())
      
      console.log('🔍 [CLIENT] 전체 게시글 조회 요청:', url.toString())
      
      const res = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })
      console.log('🔍 [CLIENT] 응답 상태:', res.status, res.statusText)
      
      const data = await res.json()
      console.log('🔍 [CLIENT] 응답 데이터:', data)
      
      if (!res.ok) throw new Error(data.error || '목록 조회 실패')
      setAll(prev => reset ? (data.results || []) : [...prev, ...(data.results || [])])
      setHasMore(data.pagination?.hasMore)
      setPage(targetPage + 1)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [loading, page, query])

  useEffect(() => {
    if (tab === 'pending') {
      setPage(1)
      setHasMore(true)
      fetchPending(true)
    } else if (tab === 'confirmed') {
      setPage(1)
      setHasMore(true)
      fetchConfirmed(true)
    } else {
      setPage(1)
      setHasMore(true)
      fetchAll(true)
    }
  }, [tab])

  const handleSearch = (e) => {
    e.preventDefault()
    setPage(1)
    setHasMore(true)
    if (tab === 'pending') {
      fetchPending(true)
    } else if (tab === 'confirmed') {
      fetchConfirmed(true)
    } else {
      fetchAll(true)
    }
  }

  const togglePostStatus = async (post_id, current_status) => {
    const new_status = current_status ? false : true
    const action = new_status ? '확정' : '미확정'
    
    console.log('🔍 [CLIENT] togglePostStatus 호출됨, post_id:', post_id, '현재상태:', current_status, '새상태:', new_status)
    console.log('🔍 [CLIENT] post_id 타입:', typeof post_id, '값:', post_id)
    try {
      const requestBody = { adminKey: ADMIN_KEY, postId: post_id, is_confirmed: new_status }
      console.log('🔍 [CLIENT] 요청 본문:', requestBody)
      
      const res = await fetch(getApiUrl(API_ENDPOINTS.ADMIN_POSTS_CONFIRM), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      })
      const data = await res.json()
      if (!res.ok || !data.success) throw new Error(data.error || `${action} 실패`)
      
      // 상태 업데이트
      if (new_status) {
        // 확정된 경우: pending에서 제거, confirmed에 추가
        setPending(prev => prev.filter(p => p.post_id !== parseInt(String(post_id).replace('post',''))))
        if (tab === 'pending') {
          fetchConfirmed(true)
        }
      } else {
        // 미확정된 경우: confirmed에서 제거, pending에 추가
        setConfirmed(prev => prev.filter(p => p.post_id !== parseInt(String(post_id).replace('post',''))))
        if (tab === 'confirmed') {
          fetchPending(true)
        }
      }
      
      // 전체 탭이면 새로고침
      if (tab === 'all') {
        fetchAll(true)
      }
    } catch (e) {
      setError(e.message)
    }
  }

  const openDetail = (post_id) => {
    const id = typeof post_id === 'number' ? `post${post_id}` : post_id
    window.open(`/admin/confirm/${id}`, '_blank')
  }

  const openYouTube = (url) => {
    window.open(url, '_blank')
  }

  const openYouTubePreview = (videoId) => {
    const previewUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1`
    window.open(previewUrl, '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes')
  }

  const openThumbnailPreview = (videoId) => {
    const thumbnailUrl = getYouTubeThumbnail(videoId)
    const popup = window.open('', '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes')
    if (popup) {
      popup.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>YouTube 썸네일 미리보기</title>
          <style>
            body { 
              margin: 0; 
              padding: 20px; 
              background: #000; 
              display: flex; 
              justify-content: center; 
              align-items: center; 
              min-height: 100vh; 
              font-family: Arial, sans-serif;
            }
            .preview-container {
              text-align: center;
              max-width: 90vw;
              max-height: 90vh;
            }
            .thumbnail-preview {
              max-width: 100%;
              max-height: 80vh;
              border-radius: 8px;
              box-shadow: 0 4px 20px rgba(0,0,0,0.5);
            }
            .preview-title {
              color: white;
              margin-top: 15px;
              font-size: 16px;
            }
            .close-btn {
              position: fixed;
              top: 20px;
              right: 20px;
              background: rgba(255,255,255,0.2);
              border: none;
              color: white;
              padding: 10px 15px;
              border-radius: 5px;
              cursor: pointer;
              font-size: 14px;
            }
            .close-btn:hover {
              background: rgba(255,255,255,0.3);
            }
          </style>
        </head>
        <body>
          <button class="close-btn" onclick="window.close()">닫기</button>
          <div class="preview-container">
            <img src="${thumbnailUrl}" alt="YouTube 썸네일" class="thumbnail-preview" onerror="this.style.display='none'; this.nextElementSibling.innerHTML='이미지를 불러올 수 없습니다.';" />
            <div class="preview-title">YouTube 썸네일 미리보기</div>
          </div>
        </body>
        </html>
      `)
      popup.document.close()
    }
  }

  // YouTube URL 미리보기 처리 함수
  const handleYoutubeUrlPreview = () => {
    if (!youtubeUrl.trim()) {
      setError('YouTube URL을 입력해주세요.')
      return
    }

    const videoId = extractVideoId(youtubeUrl.trim())
    if (!videoId) {
      setError('유효한 YouTube URL이 아닙니다.')
      return
    }

    setPreviewVideoId(videoId)
    setShowUrlPreview(true)
    setError('')
  }

  // URL 미리보기 닫기
  const closeUrlPreview = () => {
    setShowUrlPreview(false)
    setPreviewVideoId(null)
    setYoutubeUrl('')
  }

  const renderPostItem = (item) => {
    if (!item) return null;
    
    const { links: youtubeLinks, videoBlocks } = extractYouTubeLinks(item.content || '', item.blocks_json || '')
    const videoId = youtubeLinks.length > 0 ? extractVideoId(youtubeLinks[0]) : null
    

    return (
      <div key={item.post_id} className="post-item">
        <div className="post-header">
          <div className="user-info">
            <img src={item.profile_image} alt={item.user_name} className="user-avatar" />
            <div className="user-details">
              <div className="user-name">{item.user_name}</div>
              <div className="post-date">{new Date(item.created_at).toLocaleString('ko-KR')}</div>
            </div>
          </div>
          <div className="post-status">
            {item.is_confirmed ? (
              <span className="status-badge confirmed">✓ 확정됨</span>
            ) : (
              <span className="status-badge pending">⏳ 미확정</span>
            )}
          </div>
        </div>

        <div className="post-content">
          {item.title && (
            <h3 className="post-title">{item.title}</h3>
          )}
          <p className="post-text">{item.content}</p>
          
                     {youtubeLinks.length > 0 && (
             <div className="youtube-section">
               <div className="youtube-header">
                 <span className="youtube-icon">🎬</span>
                 <span className="youtube-label">YouTube 링크 ({youtubeLinks.length}개)</span>
               </div>
               <div className="youtube-links">
                 {youtubeLinks.map((link, index) => {
                   const videoBlock = videoBlocks[index];
                   const currentVideoId = extractVideoId(link);
                   
                   return (
                     <div key={index} className="youtube-link-item">
                       {currentVideoId && (
                         <div className="youtube-thumbnail">
                           <img 
                             src={getYouTubeThumbnail(currentVideoId)} 
                             alt="YouTube 썸네일"
                             onClick={() => openThumbnailPreview(currentVideoId)}
                             className="thumbnail-image"
                             onError={(e) => {
                               // 고품질 썸네일이 없으면 중간 품질로 대체
                               e.target.src = getYouTubeThumbnailFallback(currentVideoId)
                             }}
                             onLoad={() => {
                             }}
                           />
                           <div className="play-button" onClick={() => openYouTubePreview(currentVideoId)}>▶</div>
                         </div>
                       )}
                       <div className="youtube-info">
                         <a 
                           href={link} 
                           target="_blank" 
                           rel="noopener noreferrer"
                           className="youtube-url"
                         >
                           {link}
                         </a>
                         
                         {/* blocks_json에서 추가 정보 표시 */}
                         {videoBlock && (
                           <div className="video-block-info">
                             {videoBlock.embedUrl && (
                               <div className="info-item">
                                 <strong>임베드 URL:</strong> {videoBlock.embedUrl}
                               </div>
                             )}
                             {videoBlock.caption && (
                               <div className="info-item">
                                 <strong>캡션:</strong> {videoBlock.caption}
                               </div>
                             )}
                           </div>
                         )}
                         
                         <div className="youtube-actions">
                           <button 
                             className="open-youtube-btn"
                             onClick={() => openYouTube(link)}
                           >
                             YouTube에서 보기
                           </button>
                           <button 
                             className="preview-youtube-btn"
                             onClick={() => openThumbnailPreview(currentVideoId)}
                           >
                             🖼️ 썸네일 미리보기
                           </button>
                           <button 
                             className="video-preview-btn"
                             onClick={() => openYouTubePreview(currentVideoId)}
                           >
                             🎬 비디오 미리보기
                           </button>
                         </div>
                       </div>
                     </div>
                   );
                 })}
               </div>
             </div>
           )}
        </div>

        <div className="post-actions">
          <button 
            className={`action-btn ${item.is_confirmed ? 'unconfirm-btn' : 'confirm-btn'}`}
            onClick={() => {
              console.log('🔍 [CLIENT] 버튼 클릭, item:', item)
              console.log('🔍 [CLIENT] item.post_id:', item.post_id, 'item.postId:', item.postId)
              togglePostStatus(item.post_id || item.postId, item.is_confirmed)
            }}
          >
            {item.is_confirmed ? '↩️ 미확정으로' : '✓ 확정하기'}
          </button>
          <button 
            className="action-btn detail-btn" 
            onClick={() => openDetail(item.post_id)}
          >
            📋 상세보기
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="post-confirmation">
      <div className="admin-header">
        <h1>📝 글확정 관리</h1>
        <p>미확정 게시글을 확인하고 공개로 전환하세요.</p>
      </div>

      {error && (
        <div className="error-section">
          <div className="error-icon">❌</div>
          <div className="error-content">
            <h3>오류가 발생했습니다</h3>
            <p>{error}</p>
          </div>
        </div>
      )}

      <div className="search-section">
        <form className="search-form" onSubmit={handleSearch}>
          <div className="search-input-group">
            <input 
              type="text" 
              value={query} 
              onChange={(e) => setQuery(e.target.value)} 
              placeholder="제목, 내용, 작성자로 검색..."
              className="search-input"
            />
            <button type="submit" className="search-btn">
              🔍 검색
            </button>
          </div>
        </form>
      </div>

      {/* YouTube URL 미리보기 섹션 */}
      <div className="youtube-preview-section">
        <div className="youtube-preview-header">
          <h3>🎬 YouTube URL 미리보기</h3>
          <p>YouTube URL을 입력하여 썸네일과 비디오를 미리보기할 수 있습니다.</p>
        </div>
        <div className="youtube-preview-form">
          <div className="youtube-input-group">
            <input 
              type="text" 
              value={youtubeUrl} 
              onChange={(e) => setYoutubeUrl(e.target.value)} 
              placeholder="YouTube URL을 입력하세요 (예: https://www.youtube.com/watch?v=VIDEO_ID)"
              className="youtube-url-input"
            />
            <button 
              type="button" 
              onClick={handleYoutubeUrlPreview}
              className="youtube-preview-btn"
            >
              🎬 미리보기
            </button>
          </div>
        </div>
      </div>

      {/* YouTube URL 미리보기 모달 */}
      {showUrlPreview && previewVideoId && (
        <div className="youtube-preview-modal">
          <div className="youtube-preview-content">
            <div className="youtube-preview-header">
              <h3>YouTube 미리보기</h3>
              <button onClick={closeUrlPreview} className="close-modal-btn">✕</button>
            </div>
            <div className="youtube-preview-body">
              <div className="youtube-thumbnail-section">
                <h4>🖼️ 썸네일</h4>
                <div className="youtube-thumbnail-preview">
                  <img 
                    src={getYouTubeThumbnail(previewVideoId)} 
                    alt="YouTube 썸네일"
                    className="preview-thumbnail"
                    onError={(e) => {
                      e.target.src = getYouTubeThumbnailFallback(previewVideoId)
                    }}
                  />
                </div>
              </div>
              <div className="youtube-video-section">
                <h4>🎬 비디오 미리보기</h4>
                <div className="youtube-video-preview">
                  <iframe
                    src={`https://www.youtube.com/embed/${previewVideoId}?autoplay=0&rel=0`}
                    title="YouTube video player"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="preview-video"
                  ></iframe>
                </div>
              </div>
              <div className="youtube-info-section">
                <h4>📋 비디오 정보</h4>
                <div className="video-info">
                  <p><strong>비디오 ID:</strong> {previewVideoId}</p>
                  <p><strong>썸네일 URL:</strong> {getYouTubeThumbnail(previewVideoId)}</p>
                  <p><strong>원본 URL:</strong> {youtubeUrl}</p>
                </div>
              </div>
            </div>
            <div className="youtube-preview-footer">
              <button onClick={() => openYouTube(youtubeUrl)} className="open-youtube-btn">
                YouTube에서 보기
              </button>
              <button onClick={closeUrlPreview} className="close-btn">
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="tab-section">
        <div className="tab-buttons">
          <button 
            className={`tab-btn ${tab === 'pending' ? 'active' : ''}`} 
            onClick={() => setTab('pending')}
          >
            ⏳ 미확정 ({pending.length})
          </button>
          <button 
            className={`tab-btn ${tab === 'confirmed' ? 'active' : ''}`} 
            onClick={() => setTab('confirmed')}
          >
            ✓ 확정됨 ({confirmed.length})
          </button>
          <button 
            className={`tab-btn ${tab === 'all' ? 'active' : ''}`} 
            onClick={() => setTab('all')}
          >
            📋 전체 ({all.length})
          </button>
        </div>
      </div>

      <div className="posts-section">
        {tab === 'pending' && (
          <>
            <div className="posts-list">
              {pending.length === 0 && !loading && (
                <div className="empty-state">
                  <div className="empty-icon">📭</div>
                  <h3>미확정 게시글이 없습니다</h3>
                  <p>새로운 게시글이 등록되면 여기에 표시됩니다.</p>
                </div>
              )}

              {pending.map(renderPostItem)}
            </div>

            {hasMore && (
              <div className="load-more-section">
                <button 
                  className="load-more-btn" 
                  onClick={() => fetchPending(false)} 
                  disabled={loading}
                >
                  {loading ? '로딩 중...' : '더 많은 게시글 보기'}
                </button>
              </div>
            )}
          </>
        )}

        {tab === 'confirmed' && (
          <>
            <div className="posts-list">
              {confirmed.length === 0 && !loading && (
                <div className="empty-state">
                  <div className="empty-icon">✅</div>
                  <h3>확정된 게시글이 없습니다</h3>
                  <p>확정된 게시글이 여기에 표시됩니다.</p>
                </div>
              )}

              {confirmed.map(renderPostItem)}
            </div>

            {hasMore && (
              <div className="load-more-section">
                <button 
                  className="load-more-btn" 
                  onClick={() => fetchConfirmed(false)} 
                  disabled={loading}
                >
                  {loading ? '로딩 중...' : '더 많은 게시글 보기'}
                </button>
              </div>
            )}
          </>
        )}

        {tab === 'all' && (
          <>
            <div className="posts-list">
              {all.length === 0 && !loading && (
                <div className="empty-state">
                  <div className="empty-icon">📄</div>
                  <h3>게시글이 없습니다</h3>
                  <p>등록된 게시글이 없습니다.</p>
                </div>
              )}

              {all.map(renderPostItem)}
            </div>

            {hasMore && (
              <div className="load-more-section">
                <button 
                  className="load-more-btn" 
                  onClick={() => fetchAll(false)} 
                  disabled={loading}
                >
                  {loading ? '로딩 중...' : '더 많은 게시글 보기'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default PostConfirmation


